import { Component, EventEmitter, HostListener, inject, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { CodeInputComponent } from "angular-code-input";
import { map, Subject, Subscription, switchMap } from "rxjs";

import { AuthModel } from "@intocare/auth-data";

@Component({
	selector: "ic-mfa-login-form",
	templateUrl: "./mfa-login-form.component.html",
	styleUrls: ["./mfa-login-form.component.scss"],
	standalone: false,
})
export class MfaLoginFormComponent implements OnDestroy {
	private formBuilder = inject(UntypedFormBuilder);
	private authModel = inject(AuthModel);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);

	@Input() loading: boolean;
	@Output() changeToken = new EventEmitter();
	@Output() totpToken = new EventEmitter();
	@ViewChild("codeInput") codeInput!: CodeInputComponent;
	form: UntypedFormGroup;
	tokenRequired: boolean;
	toShortToken: boolean;
	mfaToken = "";
	userActivity: ReturnType<typeof setTimeout>;
	userInactive = new Subject<boolean>();
	userInactiveSubscription: Subscription;

	constructor() {
		this.form = this.formBuilder.group({
			mfaToken: [""],
		});

		this.setInactivityTimeout();
		this.userInactiveSubscription = this.userInactive.subscribe((next: boolean) => {
			if (next) {
				console.log("user has been inactive for 2 minutes");
				this.clearCode();
				this.router.navigate(["..", "sign-in-with-username"], {
					relativeTo: this.route,
					queryParams: { redirectedByInactivity: true },
				});
			}
		});
	}

	ngOnDestroy(): void {
		this.clearInactivityTimeout();
		this.userInactiveSubscription.unsubscribe();
	}

	clearInactivityTimeout() {
		if (this.userActivity) {
			// clear any existing timeout
			clearTimeout(this.userActivity);
		}
	}

	setInactivityTimeout() {
		this.clearInactivityTimeout();
		// start the timer over again 2 minutes
		this.userActivity = setTimeout(() => this.userInactive.next(true), 2 * 60 * 1000);
	}

	submit() {
		this.totpToken.emit({ token: this.mfaToken });
	}

	onLabelClick() {
		this.codeInput.focusOnField(this.mfaToken.length);
	}

	onCodeCompleted(token: string) {
		this.mfaToken = token;
		this.validate();
	}

	onCodeChanged(token: string) {
		this.mfaToken = token;
		this.changeToken.emit();
		this.tokenRequired = false;
		this.toShortToken = false;
	}

	validate() {
		this.tokenRequired = this.mfaToken === "";
		if (!isNaN(Number(this.mfaToken)) && this.mfaToken.length == 6) {
			this.submit();
		} else if (!this.tokenRequired) {
			this.toShortToken = true;
		} else {
			this.toShortToken = false;
		}
	}

	cancel() {
		this.authModel
			.logOut(false, { redirect: false })
			.pipe(
				switchMap(() => {
					return this.route.root.firstChild.paramMap;
				}),
			)
			.subscribe((params) => {
				this.router.navigate([...[params.get("org"), params.get("home")].filter(Boolean), "sign-in-with-username"]);
			});
	}

	clearCode() {
		this.codeInput.reset();
	}

	@HostListener("window:mousemove") refreshUserState() {
		this.setInactivityTimeout();
	}

	@HostListener("window:keydown") refreshUserState2() {
		this.setInactivityTimeout();
	}

	@HostListener("window:scroll") refreshUserState3() {
		this.setInactivityTimeout();
	}

	@HostListener("window:click") refreshUserState4() {
		this.setInactivityTimeout();
	}
}
